import { Eye, EyeOff, Loader2, Lock, User } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthProvider";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { getSafeRedirectPath } from "../../utils/role.utils";

type LoginFormInputs = {
  username: string;
  password: string;
  rememberMe: boolean;
  forceLogin?: boolean;
};

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const redirectTo = getSafeRedirectPath(searchParams.get("redirect"));
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [pendingLoginData, setPendingLoginData] =
    useState<LoginFormInputs | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [sessionMessage, setSessionMessage] = useState("");
  const [isJudgeSession, setIsJudgeSession] = useState(false);
  const [loading1, setLoading1] = useState(false);
  const forceLoginInProgress = useRef(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting: loading },
  } = useForm<LoginFormInputs>({
    defaultValues: {
      username: "",
      password: "",
      rememberMe: false,
    },
  });

  useEffect(() => {
    const savedUsername = localStorage.getItem("rememberedUsername");
    if (savedUsername) {
      setValue("username", savedUsername);
      setValue("rememberMe", true);
    }
  }, [setValue]);

  const onSubmit = async (data: LoginFormInputs) => {
    setShowPassword(false);
    if (data.rememberMe) {
      localStorage.setItem("rememberedUsername", data.username);
    } else {
      localStorage.removeItem("rememberedUsername");
    }

    // normal login
    const response: any = await login(
      data.username,
      data.password,
      data.forceLogin || false,
      redirectTo,
    );

    // session already active popup
    if (response?.showSessionPopup) {
      setPendingLoginData(data);

      setSessionMessage(response.message);

      setIsJudgeSession(response.isJudge);

      setShowSessionModal(true);

      return;
    }
  };

  const handleForceLogin = async () => {
    if (!pendingLoginData || forceLoginInProgress.current) return;

    forceLoginInProgress.current = true;
    setLoading1(true);

    try {
      await login(
        pendingLoginData.username,
        pendingLoginData.password,
        true,
        redirectTo,
      );
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      forceLoginInProgress.current = false;
      setShowSessionModal(false);
      setLoading1(false);
    }
  };
  useEffect(() => {
    if (!showSessionModal) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Enter") {
        event.preventDefault();
        event.stopPropagation();

        handleForceLogin();
      }

      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showSessionModal]);
  const handleCancel = () => {
    setShowSessionModal(false);
    setPendingLoginData(null);
  };
  return (
    <div className="w-full max-w-md mx-auto">
      <div className="text-center mb-8">
        <div className="bg-white  rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
          {/*<Scale className="w-8 h-8 text-white" />*/}
          <img src="logo.png" alt="NyayNet Logo" className="w-10 h-10 " />
        </div>
        <h1 className="text-3xl font-bold text-white mb-2">Welcome Back</h1>
        <p className="text-blue-100">Sign in to your NyayNet account</p>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-6"
        autoComplete="off"
      >
        {/* Username */}
        <div>
          <p className="block text-sm font-medium text-blue-100 mb-2">
            Username
          </p>
          <div className="relative">
            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-300" />
            <input
              type="text"
              placeholder="Enter username"
              className={`w-full pl-10 pr-4 py-3 bg-white/10 backdrop-blur-sm border rounded-lg text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all ${
                errors.username ? "border-red-400" : "border-white/20"
              }`}
              autoComplete="new-password"
              {...register("username", {
                required: "Username is required",
                minLength: {
                  value: 4,
                  message: "Username must be at least 4 characters long",
                },
                onChange: (e) => {
                  e.target.value = e.target.value
                    .replace(/\s/g, "")
                    .toLowerCase();
                },
              })}
            />
          </div>
          {errors.username && (
            <p className="mt-1 text-sm text-red-300">
              {errors.username.message}
            </p>
          )}
        </div>

        {/* Password */}
        <div>
          <p className="block text-sm font-medium text-blue-100 mb-2">
            Password
          </p>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-300" />
            <input
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              placeholder="Enter your password"
              className={`w-full pl-10 pr-12 py-3 bg-white/10 backdrop-blur-sm border rounded-lg text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all ${
                errors.password ? "border-red-400" : "border-white/20"
              }`}
              {...register("password", {
                pattern: {
                  value:
                    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@$%^&*()_\-+={}[\]|:;"'<>,.?/~`#]).{8,16}$/,
                  message:
                    "Password must be 8-16 chars, include uppercase, lowercase, number & special char",
                },
                required: "Password is required",
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-blue-300 hover:text-white transition-colors"
            >
              {showPassword ? (
                <Eye className="w-5 h-5" />
              ) : (
                <EyeOff className="w-5 h-5" />
              )}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1 text-sm text-red-300">
              {errors.password.message}
            </p>
          )}
        </div>

        {/* Remember me + Forgot password */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="rememberMe"
              className="w-4 h-4 text-blue-600 bg-white/10 border-white/20 rounded focus:ring-blue-500 focus:ring-2"
              {...register("rememberMe")}
            />
            <label htmlFor="rememberMe" className="text-sm text-blue-100">
              Remember me
            </label>
          </div>
          <button
            type="button"
            onClick={() => navigate("/forgot-password")}
            className="text-sm text-white underline hover:text-blue-200 transition-colors"
          >
            Forgot Password?
          </button>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-white text-slate-800 font-semibold py-3 px-6 rounded-lg hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-600 transition-all transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
        >
          {loading ? "Signing In..." : "Sign In"}
        </button>

        <div className="text-center">
          <p className="text-blue-100">
            Don't have an account?{" "}
            <button
              type="button"
              onClick={() => navigate("/signup")}
              className="text-white font-semibold underline hover:text-blue-200 transition-colors"
            >
              Sign Up
            </button>
          </p>
        </div>
      </form>
      {showSessionModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-[650px] p-8">
            {/* Heading Center */}
            <div className="mb-6 text-center">
              <h2 className="text-2xl font-semibold text-gray-800">
                {isJudgeSession
                  ? "Active Courtroom Session Detected"
                  : "Active Session Detected"}
              </h2>
            </div>

            {/* Message */}
            <p
              className={`text-center leading-7 ${
                isJudgeSession ? "font-medium text-red-600" : "text-gray-700"
              }`}
            >
              {sessionMessage}
            </p>

            {/* Buttons Center */}
            <div className="flex justify-center gap-4 mt-8">
              <button
                onClick={handleCancel}
                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {isJudgeSession ? "Cancel" : "No"}
              </button>

              <button
                type="button"
                onClick={handleForceLogin}
                disabled={loading1}
                className={`px-6 py-2 text-white rounded-lg ${
                  isJudgeSession
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {loading1 && <Loader2 className="spin" />}
                {!loading1 && (
                  <>
                    {isJudgeSession
                      ? "End Other Session & Start Here"
                      : "Yes, Continue Here"}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
